/* eslint-disable */
import * as React from 'react';
import {
    Dropdown,
    Menu,
    message,
    Spin,
    Drawer,
    Tabs,
    Tooltip,
} from 'antd';
import Helper from 'library/Helper';
import {
    PrinterOutlined,
    ExpandAltOutlined,
    LeftOutlined,
    CloseOutlined,
    InfoCircleOutlined,
    SettingOutlined,
} from '@ant-design/icons';
import { StylesProps } from '../../theme/jss-types';
import { HocOptions } from '../common/generic-hoc.types';
import GenericHoc from '../common/generic-hoc';
import { useTranslation } from 'react-i18next';
import { consignmentDetailedStyles } from './consignment-details.styles';
import { Master } from 'types/master-data-types';
import {
    downloadConsignments4X6Files,
    fetchConsignment,
    printConsignment,
    printConsignment4x6,
    printConsignment4x6Domestic,
    printInvoice,
    fetchConsignmentTracking,
} from '../../network/consignments.api';
import StatusPrettyName from './status-pretty-name';
import ConsignmentTracking from './consignment-tracking';
import ConsignmentDetailsInternational from './consignment-details-international-new';
import moment from 'moment';
import FallbackPage from '../common/fallback';
import { TrackTypeEnum, ViewData } from 'types/view.types';
import Loader from 'components/common/Loader';
import { useLocation } from 'react-router-dom';
import { getConsignmentsRoute } from 'routing/routing-helper';
import useIsMobile from 'hooks/useIsMobile';
import ConsignmentDetailsMobile from './consignment-details-mobile';
import { getDetailsView } from './view-registry';
import {
    fetchDetailsColumns as fetchDetailsColumnsAction,
    updateDetailsColumns as updateDetailsColumnsAction,
} from '../../actions/consignment-actions';
import NestedCustomColumnsModal from '../common/NestedCustomColumnsModal';
import { PAGE_NAMES } from 'types/page.types';
import { isEmpty } from 'lodash';
import { generateDetailsMetadata, ColumnMetadata } from './consignments.helper';
import { ReduxStore } from 'reducers/redux.types';

interface GroupedColumnDetail {
    column_id?: string;
    header?: string;
    columns?: Array<{
        pretty_name: string;
        id: string;
        key: string;
        type: string;
    }>;
    pretty_name?: string;
    id?: string;
    key?: string;
    type?: string;
}

interface GroupedColumnData {
    tab_header: string;
    details: GroupedColumnDetail[];
}

interface ConsignmentDetailedProps extends StylesProps<ReturnType<typeof consignmentDetailedStyles>> {
    handleGoback: () => void;
    referenceNumber: string;
    master: Master;
    toggle?: boolean;
    trackType?: string;
    showErrorMessage?: (message: string) => void;

    // Redux props for column configuration
    detailsColumnsSelected?: GroupedColumnData[];
    detailsColumnsLoading?: boolean;
    consignmentSectionList?: ViewData[][];
    fetchDetailsColumns?: () => void;
    updateDetailsColumns?: (columns: GroupedColumnData[]) => Promise<void>;
    customerIdList?: string | number;
}

const ConsignmentDetailsPage = (props: ConsignmentDetailedProps) => {
    const {
        classes, handleGoback, referenceNumber, master, trackType, toggle,
        showErrorMessage,
        detailsColumnsSelected = [],
        detailsColumnsLoading = false,
        consignmentSectionList = [],
        fetchDetailsColumns,
        updateDetailsColumns,
        customerIdList,
    } = props;
    const isMobile = useIsMobile();
    const location = useLocation();
    const isDrawerVisible = location.pathname.endsWith(getConsignmentsRoute());
    if (isDrawerVisible) {
        handleGoback();
    }
    const customerProperties = master?.properties;
    const { t, i18n } = useTranslation();
    const [expandPage, setExpandPage] = React.useState<boolean>(false);
    const [consignment, setConsignment] = React.useState<any>({});
    const [loading, setLoading] = React.useState<boolean>(true);
    const [printing, setPrinting] = React.useState(false);
    const [tracking, setTracking] = React.useState<any>({});
    const [trackingResult, setTrackingResult] = React.useState<any>({});
    const [trackHeader, setTrackHeader] = React.useState<any>();
    const [selectedTab, setSelectedTab] = React.useState('primary_details');
    const [modalVisible, setModalVisible] = React.useState(false);
    const [filteredMetadata, setFilteredMetadata] = React.useState<ColumnMetadata[]>([]);

    // Generate metadata locally from constants
    const detailsColumnsMetadata = React.useMemo(
        () => generateDetailsMetadata(consignment?.is_international || false, t, i18n),
        [consignment?.is_international, t, i18n],
    );

    const enable_edd = master?.config?.customer_portal_config?.enable_edd !== false;
    const enable_customer_config = master?.parts_to_show?.enable_customer_config || false;
    let showShipmentLabelA4 = true;
    let showShippingLabelA6 = true;
    let showshippingLabelPod = true;
    let showShippingLabel4X6 = true;
    let restrictPrintInvoice = false;
    let useThirdPartyLabel = false;
    if (customerProperties && enable_customer_config) {
        showShipmentLabelA4 = customerProperties?.a4_shipping_label;
        showShippingLabelA6 = customerProperties?.a6_shipping_label;
        showshippingLabelPod = customerProperties?.shipping_label_pod;
        showShippingLabel4X6 = customerProperties?.x46_shipping_label;
        restrictPrintInvoice = !customerProperties?.print_invoice;
        useThirdPartyLabel = customerProperties?.use_third_party_label;
    }

    const showShippingLabelButton = showShipmentLabelA4 || showShippingLabelA6
    || showshippingLabelPod || showShippingLabel4X6;

    const piecesArray = consignment?.pieces_detail;
    const detailsTabs = [
        'primary_details',
        piecesArray?.length > 0 ? 'pieces_details' : null,
        'address_details',
    ].filter(Boolean);

    const loadTracking = async () => {
        setLoading(true);
        const response = await fetchConsignment({ reference_number: referenceNumber });
        setConsignment(response.data?.[0]);

        const customerId = response?.data?.[0]?.customer_id;

        let trackingResponse;
        const searchNumber = referenceNumber;
        switch (trackType) {
            case TrackTypeEnum.CUSTOMER_REFERENCE_NO:
                trackingResponse = await fetchConsignmentTracking({ customerReferenceNumber: searchNumber, userId: customerId });
                break;
            case TrackTypeEnum.CARRIER_AWB_NUMBER:
                trackingResponse = await fetchConsignmentTracking({ courierPartnerReferenceNumber: searchNumber, userId: customerId });
                break;
            default:
                trackingResponse = await fetchConsignmentTracking({ referenceNumber: searchNumber, userId: customerId });
        }

        const trackingData = (trackingResponse?.data?.events || []);
        const trackingHeader = trackingResponse?.data?.track_header;

        if (setTrackHeader) {
            setTrackHeader(trackingHeader);
        }
        setTrackingResult(trackingResponse);
        setTracking(trackingData || []);
        setLoading(false);
        if (!trackingData.length && showErrorMessage) {
            showErrorMessage(trackingResponse?.errorMessage || t('tracking_fallback_message'));
            handleGoback();
        }
    };

    React.useEffect(() => {
        loadTracking();
    }, [toggle]);

    // Fetch column details on mount
    React.useEffect(() => {
        if (fetchDetailsColumns) {
            fetchDetailsColumns();
        }
    }, []);

    // Filter metadata based on view data
    React.useEffect(() => {
        const viewId = new URLSearchParams(location.search || '')?.get('view');

        if (viewId && consignmentSectionList && detailsColumnsMetadata.length > 0) {
            // Extract all column keys from metadata organized by tab_header
            const getDetailsToShowKeyList = (metadata: ColumnMetadata[]): Record<string, (string | undefined)[]> => {
                const detailsToShowKeysList: Record<string, (string | undefined)[]> = {};

                metadata.forEach((tab) => {
                    const tabHeader = tab.tab_header;
                    const keys: (string | undefined)[] = [];

                    tab.details?.forEach((detail) => {
                        detail.columns?.forEach((column) => {
                            const columnId = column.id || column.key;
                            if (columnId) {
                                keys.push(columnId);
                            }
                        });
                    });

                    if (keys.length > 0) {
                        detailsToShowKeysList[tabHeader] = keys;
                    }
                });

                return detailsToShowKeysList;
            };

            const viewData = consignmentSectionList?.flat().find((view) => view.id === viewId);

            const consignmentFieldList: Record<string, string> = viewData?.metadata?.fieldList?.reduce(
                (obj: Record<string, string>, item) => {
                    if (item?.enable) {
                        return { ...obj, [item.id]: item.name };
                    }
                    return obj;
                },
                {},
            ) || {};

            const viewDataKeys: Record<string, (string | undefined)[]> = {};

            if (!isEmpty(consignmentFieldList)) {
                const detailsToShowKeysList = getDetailsToShowKeyList(detailsColumnsMetadata);
                Object.keys(detailsToShowKeysList).forEach((key) => {
                    viewDataKeys[key] = detailsToShowKeysList[key]?.filter(
                        (item) => item && consignmentFieldList?.[item],
                    ) || [];
                });

                // Filter metadata to only include columns present in viewDataKeys
                const filteredMetadataResult = detailsColumnsMetadata.map((tab) => {
                    const tabHeader = tab.tab_header;
                    const allowedKeys = viewDataKeys[tabHeader] || [];

                    const filteredDetails = tab.details?.map((detail) => {
                        const filteredColumns = (detail.columns || [])
                            .filter((column) => {
                                const columnId = column.id || column.key;
                                return columnId && allowedKeys.includes(columnId);
                            })
                            .map((column) => {
                                const columnId = column.id || column.key;
                                // Replace pretty_name with name from view data if available
                                const viewDataName = columnId ? consignmentFieldList[columnId] : null;
                                return {
                                    ...column,
                                    pretty_name: viewDataName || column.pretty_name,
                                };
                            });

                        return {
                            ...detail,
                            columns: filteredColumns,
                        };
                    }).filter((detail) => detail.columns && detail.columns.length > 0) || [];

                    return {
                        ...tab,
                        details: filteredDetails,
                    };
                }).filter((tab) => tab.details && tab.details.length > 0);

                setFilteredMetadata(filteredMetadataResult);
            } else {
                setFilteredMetadata(detailsColumnsMetadata);
            }
        } else {
            setFilteredMetadata(detailsColumnsMetadata);
        }
    }, [consignmentSectionList, detailsColumnsMetadata, location.search]);

    // Modal handlers
    const handleGearClick = () => {
        setModalVisible(true);
    };

    const handleModalSubmit = async (groupedData: GroupedColumnData[]) => {
        if (updateDetailsColumns) {
            try {
                await updateDetailsColumns(groupedData);
                return { isSuccess: true };
            } catch (error) {
                return { isSuccess: false };
            }
        }
        return { isSuccess: false };
    };

    const handleModalCancel = () => {
        setModalVisible(false);
    };

    const menuStyle: React.CSSProperties = {
        padding: 12,
        borderBottom: '1px solid #CCCCCC',
        fontWeight: 'bold',
        fontSize: 12,
    };

    const handlePrintInvoiceBuffer = (fileBuffer: any, fileName: string) => {
        if (fileBuffer.isSuccess) {
            const name = fileBuffer.filename || fileName;
            Helper.downloadFileData(
                fileBuffer.data,
                name,
                true,
                'pdf',
            );
            message.success('Success');
        } else {
            message.error('Unable to fetch Invoice at the moment. Please try again after sometime');
        }
        setPrinting(false);
    };

    const handlePrintLabel = (fileBuffer: any, fileName: string) => {
        if (fileBuffer.isSuccess) {
            const name = fileBuffer.filename || fileName;
            Helper.downloadFileData(
                fileBuffer.data,
                name,
                true,
                'pdf',
            );
            message.success('Success');
        } else {
            message.error('Unable to fetch label at the moment. Please try again after sometime');
        }
        setPrinting(false);
    };

    const handlePrint = async (referenceNumbers: any, isSmall: boolean, fileName: string) => {
        setPrinting(true);
        const fileBuffer = await printConsignment({
            isSmall,
            referenceNumbers,
            useThirdPartyLabel,
        });
        handlePrintLabel(fileBuffer, fileName);
    };


    const handlePrint4X6 = async (referenceNumbers: any) => {
        setPrinting(true);
        const fileUrls = await printConsignment4x6({
            referenceNumbers,
        });
        fileUrls?.data?.forEach((fileUrlsList: any) => {
            fileUrlsList?.forEach(async (fileUrl: any) => {
                if (fileUrl && fileUrl.length) {
                    const fileBuffer = await downloadConsignments4X6Files(fileUrl);
                    const fileUrlSplit = fileUrl.split('/');
                    handlePrintLabel(fileBuffer, fileUrlSplit[fileUrlSplit.length - 1]);
                }
            });
        });
        setPrinting(false);
    };

    const handlePrint4X6Domestic = async (referenceNumbers: any) => {
        setPrinting(true);
        const fileBuffer = await printConsignment4x6Domestic({
            referenceNumbers,
        });
        handlePrintLabel(fileBuffer, `Shipping_Label_4x6_${referenceNumbers[0]}.pdf`);
    };

    const handleOnClickPrint4X6 = async (referenceNumbers: any, isInternational: boolean) => {
        if (isInternational) {
            await handlePrint4X6(referenceNumbers);
        } else {
            await handlePrint4X6Domestic(referenceNumbers);
        }
    };

    const handlePrintInvoice = async (referenceNumbers: any, fileName: string) => {
        setPrinting(true);
        const fileBuffer = await printInvoice({
            referenceNumbers,
        });
        handlePrintInvoiceBuffer(fileBuffer, fileName);
    };

    const renderPrintInvoice = (row: any) => {
        if (
            !row.is_international
            || !master.Customer.show_invoice_options
            || row.load_type === 'DOCUMENT'
            || restrictPrintInvoice
        ) {
            return null;
        }
        return (
            <Menu.Item
                style={menuStyle}
                onClick={() => handlePrintInvoice(
                    [row.reference_number],
                    `Invoice_${row.reference_number}.pdf`,
                )}
            >
                {t('print_invoice')}
            </Menu.Item>
        );
    };

    const renderPrintOptions = (row: any) => {
        return (
            <Menu>
                <Menu.Item
                    style={{ ...menuStyle, padding: 0 }}
                >
                    {showShippingLabelButton && (
                        <Menu.SubMenu
                            title={(
                                <span
                                    style={{
                                        ...menuStyle,
                                        padding: 0,
                                        border: 'none',
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    {t('shipping_label')}
                                </span>
                            )}
                            style={{
                                ...menuStyle,
                                padding: '6px 0px',
                                border: 'none',
                            }}
                        >
                            {showShipmentLabelA4 && (
                                <Menu.Item
                                    style={menuStyle}
                                    onClick={() => handlePrint(
                                        [row.reference_number],
                                        false,
                                        `Shipping_Label_${row.reference_number}.pdf`,
                                    )}
                                >
                                    {t('label_a4')}
                                </Menu.Item>
                            )}
                            {showShippingLabelA6 && (
                                <Menu.Item
                                    style={menuStyle}
                                    onClick={() => handlePrint(
                                        [row.reference_number],
                                        true,
                                        `Shipping_Label_${row.reference_number}.pdf`,
                                    )}
                                >
                                    A6
                                </Menu.Item>
                            )}
                            {showShippingLabel4X6 && (
                                <Menu.Item
                                    style={menuStyle}
                                    onClick={() => handleOnClickPrint4X6([row.reference_number], row.is_international)}
                                >
                                    4X6
                                </Menu.Item>
                            )}
                        </Menu.SubMenu>
                    )}
                </Menu.Item>
                {renderPrintInvoice(row)}
            </Menu>
        );
    };

    const renderPrint = () => {
        const showPrintIcon = showShippingLabelButton
        || !(!consignment.is_international
            || !master.Customer.show_invoice_options
            || consignment.load_type === 'DOCUMENT'
            || restrictPrintInvoice);

        if (!showPrintIcon) {
            return null;
        }

        if (printing) {
            return <Spin />;
        }
        return !useThirdPartyLabel ? (
            <Dropdown
                disabled={printing}
                overlay={renderPrintOptions(consignment)}
                trigger={isMobile ? ['click'] : ['hover']}
            >
                <PrinterOutlined className={classes.printerIcon} />
            </Dropdown>
        ) : (
            <PrinterOutlined
                className={classes.printerIcon}
                onClick={() => {
                    handlePrint(
                        [consignment.reference_number],
                        false,
                        `Shipping_Label_${consignment.reference_number}.pdf`,
                    );
                }}
            />
        );
    };

    const renderHeader = () => {
        if (loading) {
            return null;
        }

        return (
            <div className={expandPage ? classes.pageHeader : classes.drawerHeader}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    {!expandPage ? (
                        <CloseOutlined
                            onClick={() => handleGoback()}
                            alt="close"
                        />
                    ) : (
                        <LeftOutlined
                            onClick={() => handleGoback()}
                            alt="close"
                        />
                    )}
                    <div className={classes.consignmentHeader}>{consignment.reference_number}</div>
                    {!expandPage
                        && (
                            <div className={classes.expandIcon}>
                                <ExpandAltOutlined onClick={() => setExpandPage(true)} />
                            </div>
                        )}
                    <span className={classes.consignmentStatus}>
                        {t('status')} : <StatusPrettyName status={consignment.status} />
                    </span>
                    <span className={classes.consignmentServiceType}>
                        {`${t('service_type')} : 
                        ${consignment[consignment.is_international ? 'booking_service_type_id' : 'service_type']}`}
                    </span>
                    <span className={classes.paymentMode}>
                        {consignment.is_cod
                            ? t('cod') : t('prepaid')}
                    </span>
                    {consignment.is_cod
                        ? (
                            <span className={classes.consignmentServiceType}>
                                {`${t('cod_amount')} : `}
                                {consignment.cod_amount ? consignment.cod_amount : 'N/A'}
                            </span>
                        ) : null }
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <SettingOutlined
                        className={classes.printerIcon}
                        onClick={handleGearClick}
                        style={{ cursor: 'pointer' }}
                    />
                    {renderPrint()}
                </div>
            </div>
        );
    };

    if (!tracking.length && !loading) {
        if (trackingResult && !!trackingResult.errorMessage) {
            return <FallbackPage errorMsg={trackingResult.errorMessage} />;
        }
        return <FallbackPage errorMsg={t('tracking_fallback_message')} />;
    }

    const getDate = (input: string) => {
        if (!input) {
            return null;
        }
        return moment(input).format('DD MMM YYYY');
    };

    const renderEddDates = () => {
        return (
            <div className={classes.trackText}>
                {(enable_edd && trackingResult?.data?.expected_delivery_date) || trackHeader?.edd_date ? (
                    <>
                        <div className={classes.eddDates}>
                            <Tooltip title="Expected Delivery Date">
                                <InfoCircleOutlined className={classes.eddDescription} />
                            </Tooltip>
                            <span>EDD :</span>
                            <span className={classes.eddValue}>
                                {(() => {
                                    if (enable_edd && trackingResult?.data?.expected_delivery_date) {
                                        return getDate(trackingResult?.data?.expected_delivery_date);
                                    } if (trackHeader?.edd_date) {
                                        return getDate(trackHeader.edd_date);
                                    }
                                    return 'N/A';
                                })()}
                            </span>
                        </div>
                    </>
                ) : null}

                {(enable_edd && trackingResult?.data?.revised_expected_delivery_date) || trackHeader?.redd_date ? (
                    <>
                        <div className={classes.eddDates}>
                            <Tooltip title="Revised Expected Delivery Date">
                                <InfoCircleOutlined className={classes.eddDescription} />
                            </Tooltip>
                            <span>REDD :</span>
                            <span className={classes.eddValue}>
                                {(() => {
                                    if (enable_edd) {
                                        return getDate(trackingResult?.data?.revised_expected_delivery_date);
                                    } if (trackHeader?.redd_date) {
                                        return getDate(trackHeader.redd_date);
                                    }
                                    return 'N/A';
                                })()}
                            </span>
                        </div>
                    </>
                ) : null}
            </div>

        );
    };

    const renderTracking = () => {
        return (
            <div className={classes.trackingBox}>
                <ConsignmentTracking
                    referenceNumber={referenceNumber}
                    renderEddDates={renderEddDates}
                    tracking={tracking}
                />
            </div>
        );
    };

    const renderQuickDetails = () => {
        if (loading) {
            return null;
        }
        return (
            <div className={classes.quickDetails}>
                <div className={classes.quickDetailsItem}>
                    <span className={classes.quickDetailsValue}>{consignment.customer_reference_number || 'N/A'}</span>
                    <span className={classes.quickDetailsLabel}>{t('customer_reference_no')}</span>
                </div>
                <div className={classes.verticalDash} />
                <div className={classes.quickDetailsItem}>
                    <span className={classes.quickDetailsValue}>{moment(consignment.created_at).format('DD MMM YYYY')}</span>
                    <span className={classes.quickDetailsLabel}>{t('created_at_ltl')}</span>
                </div>
                <div className={classes.verticalDash} />
                <div className={classes.quickDetailsItem}>
                    <span className={classes.quickDetailsValue}>{consignment.pickup_attempt_count}</span>
                    <span className={classes.quickDetailsLabel}>{t('pickup_attempts')}</span>
                </div>
                <div className={classes.verticalDash} />
                <div className={classes.quickDetailsItem}>
                    <span className={classes.quickDetailsValue}>{consignment.attempt_count}</span>
                    <span className={classes.quickDetailsLabel}>{t('delivery_attempts')}</span>
                </div>
            </div>
        );
    };

    const renderTabs = () => {
        return (
            <div className={classes.tabsContainer}>
                <Tabs
                    className={classes.bucketRow}
                    activeKey={selectedTab}
                    onChange={(tab) => setSelectedTab(tab)}
                >
                    {detailsTabs.map((tab) => (
                        <Tabs.TabPane
                            tab={t(tab as string)}
                            key={tab}
                        />
                    ))}
                </Tabs>
            </div>
        );
    };

    const renderDetails = () => {
        if (loading) {
            return <Loader zIndex={10} />;
        }
        return (
            <div className={classes.bodyContainer}>
                <div className={classes.leftColumn}>
                    {renderTracking()}
                </div>
                <div className={classes.rightColumn}>
                    {renderQuickDetails()}
                    {renderTabs()}
                    <ConsignmentDetailsInternational
                        consignment={consignment}
                        selectedTab={selectedTab}
                        expandPage={expandPage}
                        detailsColumnsMetadata={filteredMetadata.length > 0 ? filteredMetadata : detailsColumnsMetadata}
                        detailsColumnsSelected={detailsColumnsSelected}
                        detailsColumnsLoading={detailsColumnsLoading}
                    />
                </div>
            </div>
        );
    };

    const viewId = master?.config?.customer_portal_config?.page_views?.consignment_details?.view_id;
    const viewIdMobile = master?.config?.customer_portal_config?.page_views?.consignment_details?.view_id_mobile;
    const viewIdToUse = isMobile ? (viewIdMobile || viewId) : viewId;

    if (viewIdToUse) {
        const CustomView = getDetailsView(viewIdToUse);
        if (CustomView) {
            return (
                <CustomView
                    handleGoback={handleGoback}
                    referenceNumber={referenceNumber}
                    master={master}
                    toggle={toggle}
                    trackType={trackType}
                    showErrorMessage={showErrorMessage}
                    consignment={consignment}
                    loading={loading}
                    tracking={tracking}
                    trackingResult={trackingResult}
                    expandPage={expandPage}
                    setExpandPage={setExpandPage}
                    detailsColumnsMetadata={filteredMetadata.length > 0 ? filteredMetadata : detailsColumnsMetadata}
                    detailsColumnsSelected={detailsColumnsSelected}
                    detailsColumnsLoading={detailsColumnsLoading}
                />
            );
        }
    }

    if (isMobile) {
        return (
            <ConsignmentDetailsMobile
                loading={loading}
                handleGoback={handleGoback}
                consignment={consignment}
                renderPrint={renderPrint}
                tracking={tracking}
            />
        );
    }

    return (
        <>
            {expandPage ? (
                <div className={classes.mainPage}>
                    {renderHeader()}
                    {renderDetails()}
                </div>
            ) : (
                <Drawer
                    className={classes.mainDrawer}
                    visible
                    width="80%"
                    title={renderHeader()}
                    onClose={handleGoback}
                    closable={false}
                >
                    {renderDetails()}
                </Drawer>
            )}

            {modalVisible && (
                <NestedCustomColumnsModal
                    isVisible={modalVisible}
                    onModalClose={handleModalCancel}
                    onSubmit={handleModalSubmit}
                    detailsColumnsMetadata={(filteredMetadata.length > 0 ? filteredMetadata : detailsColumnsMetadata).filter(
                        (tab) => tab.tab_header !== 'Address Details',
                    )}
                    detailsColumnsSelected={detailsColumnsSelected.filter(
                        (tab) => tab.tab_header !== 'Address Details',
                    )}
                />
            )}
        </>
    );
};

const mapStateToProps = (state: ReduxStore) => ({
    detailsColumnsSelected: state.generic.CONSIGNMENTS?.detailsColumns?.selectedColumns || [],
    detailsColumnsLoading: state.generic.CONSIGNMENTS?.detailsColumns?.loading || false,
    consignmentSectionList: state.genericPage?.[PAGE_NAMES.CONSIGNMENTS]?.metadata?.sectionList,
    customerIdList: state.generic.CONSIGNMENTS?.appliedFilters?.customerIdList,
});

const mapDispatchToProps = (dispatch: any) => ({
    fetchDetailsColumns: () => dispatch(fetchDetailsColumnsAction()),
    updateDetailsColumns: (columns: GroupedColumnData[]) => dispatch(updateDetailsColumnsAction(columns as any)),
});

const hocConfig: HocOptions = {
    connectJss: {
        useJss: true,
        styleSheet: consignmentDetailedStyles,
    },
    connectRedux: {
        useRedux: true,
        mapStateToProps,
        mapDispatchToProps,
    },
};
const ConsignmentDetailsNew = GenericHoc(hocConfig)(ConsignmentDetailsPage);

export default ConsignmentDetailsNew;
